import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Verification token is missing.');
        return;
      }

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage(data.message);
          // Auto redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed. Please try again.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Network error. Please check your connection and try again.');
      }
    };

    verifyEmail();
  }, [token, navigate]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center">
          {/* Status Icon */}
          <div className="mb-6">
            {status === 'verifying' && (
              <div className="mx-auto h-16 w-16 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Loader className="h-8 w-8 text-blue-400 animate-spin" />
              </div>
            )}
            
            {status === 'success' && (
              <div className="mx-auto h-16 w-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-400" />
              </div>
            )}
            
            {status === 'error' && (
              <div className="mx-auto h-16 w-16 bg-red-500/20 rounded-full flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-4">
            {status === 'verifying' && 'Verifying Your Email...'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </h1>

          {/* Message */}
          <p className={`mb-6 ${
            status === 'success' ? 'text-green-300' : 
            status === 'error' ? 'text-red-300' : 
            'text-gray-300'
          }`}>
            {message}
          </p>

          {/* Actions */}
          <div className="space-y-4">
            {status === 'success' && (
              <>
                <p className="text-sm text-gray-400">
                  Redirecting to login page in 3 seconds...
                </p>
                <Link
                  to="/login"
                  className="block w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-colors"
                >
                  Login Now
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <Link
                  to="/register"
                  className="block w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-colors"
                >
                  Try Registering Again
                </Link>
                <Link
                  to="/login"
                  className="block w-full border border-purple-500 text-purple-400 hover:text-white hover:bg-purple-500 font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Back to Login
                </Link>
              </>
            )}

            {status === 'verifying' && (
              <p className="text-sm text-gray-400">
                Please wait while we verify your email address...
              </p>
            )}
          </div>

          {/* Help Text */}
          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-sm text-gray-400">
              Need help?{' '}
              <Link to="/contact" className="text-purple-400 hover:text-purple-300">
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;